import { useEffect, useRef, useState, useCallback } from 'react';
import { parseGooglePlaceAddress } from '../utils/googlePlaceAddress';

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

export function useGooglePlaceMap({
    mapRef,
    inputRef,
    onPlaceSelected,
    initialCenter,
    enabled = true,
    activeKey,
}) {
    const markersRef = useRef([]);
    const [confirmedPlace, setConfirmedPlace] = useState('');

    const handlePlace = useCallback((place) => {
        if (!place?.geometry) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = parseGooglePlaceAddress(place);

        setConfirmedPlace(place.formatted_address || '');
        onPlaceSelected?.({
            place: place.formatted_address || '',
            latitude: lat,
            longitude: lng,
            map_url: place.url || '',
            ...address,
        });
    }, [onPlaceSelected]);

    useEffect(() => {
        if (!enabled) return undefined;

        let cancelled = false;
        let listener = null;
        let retryTimer = null;

        const initMap = () => {
            if (cancelled) return;
            if (!mapRef?.current || !inputRef?.current || !window.google?.maps?.places) {
                retryTimer = setTimeout(initMap, 300);
                return;
            }

            const center = initialCenter || DEFAULT_CENTER;
            const map = new window.google.maps.Map(mapRef.current, {
                center,
                zoom: 12,
                mapTypeControl: false,
                streetViewControl: false,
            });

            const searchBox = new window.google.maps.places.SearchBox(inputRef.current);
            map.addListener('bounds_changed', () => {
                searchBox.setBounds(map.getBounds());
            });

            listener = searchBox.addListener('places_changed', () => {
                const places = searchBox.getPlaces();
                if (!places?.length) return;

                markersRef.current.forEach((marker) => marker.setMap(null));
                markersRef.current = [];

                const bounds = new window.google.maps.LatLngBounds();
                places.forEach((place) => {
                    if (!place.geometry) return;
                    handlePlace(place);
                    markersRef.current.push(
                        new window.google.maps.Marker({
                            map,
                            title: place.name,
                            position: place.geometry.location,
                        })
                    );
                    if (place.geometry.viewport) {
                        bounds.union(place.geometry.viewport);
                    } else {
                        bounds.extend(place.geometry.location);
                    }
                });
                map.fitBounds(bounds);
            });
        };

        initMap();

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
            if (listener && window.google?.maps?.event) {
                window.google.maps.event.removeListener(listener);
            }
        };
    }, [mapRef, inputRef, handlePlace, initialCenter, enabled, activeKey]);

    return { confirmedPlace, setConfirmedPlace };
}
